import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCourses, createCourse, updateCourse, deleteCourse, getInstructors, getStudents, enrollStudent, unenrollStudent } from '../../../api/adminApi';
import Badge from '../../../components/admin/Badge';
import Toast from '../../../components/admin/Toast';
import ConfirmModal from '../../../components/admin/ConfirmModal';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import PageLoader from '../../../components/common/PageLoader';
import { toTitleCase } from '../../../utils/format';
import { sanitizeFields } from '../../../utils/sanitize';

const AV_COLORS = ['#E8440A','#F7C91B','#1A7048','#5B6ABF','#D4882A','#8A6FB0','#C74D3A','#2E8B8B'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }

const EMPTY_FORM = { title: '', code: '', capacity: 30, start_date: '', end_date: '', status: 'Draft', emoji: '📚', description: '' };
const PAGE_SIZE  = 20;

export default function CourseList() {
  const [courses,          setCourses]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [modal,            setModal]            = useState(null);
  const [toast,            setToast]            = useState(null);
  const [form,             setForm]             = useState(EMPTY_FORM);
  const [saving,           setSaving]           = useState(false);
  const [instructors,      setInstructors]      = useState([]);
  const [students,         setStudents]         = useState([]);
  const [selectedInst,     setSelectedInst]     = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [originalStudents, setOriginalStudents] = useState([]);
  const [page,             setPage]             = useState(1);
  const [search,           setSearch]           = useState('');
  const [deleteTarget,     setDeleteTarget]     = useState(null); // { id, title }
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    getCourses()
      .then(d => { setCourses(d.courses); setPage(1); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered    = courses.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));
  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ── New course ── */
  async function handleCreate(e) {
    e.preventDefault();
    if (form.start_date && form.end_date && form.start_date >= form.end_date) {
      setToast({ msg: 'Start date must be before end date.', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await createCourse(sanitizeFields({ ...form }, ['title', 'code', 'description']));
      setModal(null);
      setForm(EMPTY_FORM);
      setToast('Course created successfully');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  /* ── Assign instructor ── */
  async function openAssignInstructor(e, course) {
    e.stopPropagation();
    const { instructors: list } = await getInstructors();
    setInstructors(list);
    setSelectedInst(course.instructor?.id ? String(course.instructor.id) : '');
    setModal({ type: 'assign-inst', course });
  }
  async function handleAssignInstructor() {
    if (selectedInst) {
      const inst = instructors.find(i => String(i.id) === String(selectedInst));
      if (inst && inst.status !== 'Active') {
        setToast({ msg: 'Cannot assign an inactive instructor.', type: 'error' });
        return;
      }
    }
    setSaving(true);
    try {
      await updateCourse(modal.course.id, { instructor_id: selectedInst || null });
      setModal(null);
      setToast('Instructor assigned');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  /* ── Assign students ── */
  async function openAssignStudents(e, course) {
    e.stopPropagation();
    const { students: list } = await getStudents();
    const enrolled = (course.enrolled_student_ids || []).map(Number);
    setStudents(list);
    setSelectedStudents(enrolled);
    setOriginalStudents(enrolled);
    setModal({ type: 'enroll', course });
  }
  async function handleEnroll() {
    setSaving(true);
    try {
      const courseId   = modal.course.id;
      const toEnroll   = selectedStudents.filter(id => !originalStudents.includes(id));
      const toUnenroll = originalStudents.filter(id => !selectedStudents.includes(id));
      await Promise.all([
        ...toEnroll.map(id => enrollStudent({ student_id: id, course_id: courseId })),
        ...toUnenroll.map(id => unenrollStudent(id, courseId)),
      ]);
      setModal(null);
      setToast('Students updated');
      load();
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Courses</div>
          <div className="pt">Course Management</div>
          <div className="ps">
            {search
              ? `${filtered.length} of ${courses.length} course${courses.length !== 1 ? 's' : ''}`
              : `${courses.length} course${courses.length !== 1 ? 's' : ''} on the platform`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            className="li"
            style={{ width: 220, margin: 0 }}
            placeholder="Search by title…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <button className="btn btn-dark btn-sm" onClick={() => setModal('new')}>+ New Course</button>
        </div>
      </div>

      {/* ── Course Grid ── */}
      <div className="body">
        {loading ? <PageLoader /> : filtered.length === 0 ? (
          <div className="card">
            <div className="empty">
              <div className="empty-icon">📚</div>
              <div>{search ? `No courses matching "${search}"` : 'No courses yet. Create your first course.'}</div>
            </div>
          </div>
        ) : (<>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 14 }}>
            {paginated.map(c => {
              const color = c.accent_color || '#5B6ABF';
              const pct   = c.modules_total > 0 ? Math.round((c.modules_published / c.modules_total) * 100) : 0;
              return (
                <div
                  key={c.id}
                  className="cc"
                  onClick={() => navigate(`/admin/courses/${c.id}`)}
                >
                  {/* Coloured thumb */}
                  <div
                    className="cc-thumb"
                    style={{ background: `linear-gradient(135deg, ${color}22, ${color}44)` }}
                  >
                    <span style={{ fontSize: 44 }}>{c.emoji || '📚'}</span>
                    <div className="cc-tag">
                      {c.assignments_count ?? 0} assignment{(c.assignments_count ?? 0) !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="cc-body">
                    <div className="cc-title">{toTitleCase(c.title)}</div>
                    <div className="cc-meta">
                      {toTitleCase(c.instructor?.full_name || 'Unassigned')}
                      {' · '}{c.enrolled_count} student{c.enrolled_count !== 1 ? 's' : ''}
                      {' · '}{c.modules_published}/{c.modules_total} modules published
                    </div>
                    {/* Actions row */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        className="btn btn-p btn-sm"
                        onClick={e => { e.stopPropagation(); navigate(`/admin/courses/${c.id}`); }}
                      >
                        Manage
                      </button>
                      <span style={{ marginLeft: 'auto' }}><Badge status={c.status} /></span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>)}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 20 }}>
            <button
              className="btn btn-o btn-sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ← Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                className={`btn btn-sm ${n === page ? 'btn-p' : 'btn-o'}`}
                onClick={() => setPage(n)}
                style={{ minWidth: 36 }}
              >
                {n}
              </button>
            ))}

            <button
              className="btn btn-o btn-sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next →
            </button>

            <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
          </div>
        )}
      </div>

      {/* ── New Course Modal ── */}
      {modal === 'new' && (
        <div className="modal-overlay">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              New Course
              <button className="btn btn-o btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="fg">
                <label className="fl">Course Title</label>
                <input className="li" placeholder="e.g. AI For Marketing" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Code</label>
                  <input className="li" placeholder="MKT-301" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required />
                </div>
                <div className="fg">
                  <label className="fl">Capacity</label>
                  <input className="li" type="number" min={1} value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} />
                </div>
              </div>
              <div className="fg">
                <label className="fl">Description</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={form.description || ''}
                  onChange={(_, editor) => setForm(f => ({ ...f, description: editor.getData() }))}
                  config={{ toolbar: ['bold','italic','|','bulletedList','numberedList','|','blockQuote','link'] }}
                />
              </div>
              <div className="fg">
                <label className="fl">Status</label>
                <select className="li" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option>Draft</option>
                  <option>Published</option>
                  <option>Archived</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="fg">
                  <label className="fl">Start Date</label>
                  <input className="li" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} required />
                </div>
                <div className="fg">
                  <label className="fl">End Date</label>
                  <input className="li" type="date" value={form.end_date} min={form.start_date || undefined} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} required />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-o" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-p" disabled={saving}>{saving ? 'Creating…' : 'Create Course'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Assign Instructor Modal ── */}
      {modal?.type === 'assign-inst' && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Assign Instructor
              <button className="btn btn-o btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, marginTop: -10 }}>
              Select instructor to assign to <strong style={{ color: 'var(--ink)' }}>{toTitleCase(modal.course.title)}</strong>
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              {instructors.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0' }}>No instructors available</div>
              ) : (
                <div className="course-pick-list">
                  {instructors.map(i => {
                    const sel      = String(selectedInst) === String(i.id);
                    const inactive = i.status !== 'Active';
                    return (
                      <div
                        key={i.id}
                        className={`course-pick-item${sel ? ' selected' : ''}`}
                        style={{ opacity: inactive ? 0.45 : 1, cursor: inactive ? 'not-allowed' : 'pointer' }}
                        title={inactive ? 'Inactive instructor — cannot be assigned' : ''}
                        onClick={() => { if (!inactive) setSelectedInst(sel ? '' : String(i.id)); }}
                      >
                        <div className="course-pick-check">{sel ? '✓' : ''}</div>
                        <div className="av" style={{ width: 28, height: 28, background: avColor(i.id), color: 'white', fontSize: 9, flexShrink: 0 }}>
                          {initials(i.full_name)}
                        </div>
                        <div>
                          <span style={{ fontSize: 13.5, fontWeight: 500 }}>{toTitleCase(i.full_name)}</span>
                          {inactive && <span className="b b-r" style={{ marginLeft: 8, fontSize: 10 }}>Inactive</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-o" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-p" onClick={handleAssignInstructor} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Students Modal ── */}
      {modal?.type === 'enroll' && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Assign Students
              <button className="btn btn-o btn-sm" onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, marginTop: -10 }}>
              Select students to assign to <strong style={{ color: 'var(--ink)' }}>{toTitleCase(modal.course.title)}</strong>
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              {students.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0' }}>No students available</div>
              ) : (
                <div className="course-pick-list">
                  {students.map(s => {
                    const sel = selectedStudents.includes(s.id);
                    return (
                      <div
                        key={s.id}
                        className={`course-pick-item${sel ? ' selected' : ''}`}
                        onClick={() => setSelectedStudents(prev =>
                          sel ? prev.filter(id => id !== s.id) : [...prev, s.id]
                        )}
                      >
                        <div className="course-pick-check">{sel ? '✓' : ''}</div>
                        <div className="av" style={{ width: 28, height: 28, background: avColor(s.id), color: 'white', fontSize: 9, flexShrink: 0 }}>
                          {initials(s.full_name)}
                        </div>
                        <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.full_name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-o" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-p" onClick={handleEnroll} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Course?"
          message="This course will be removed from the platform. Enrolled students will immediately lose access to all modules, quizzes and assignments."
          confirmLabel="Yes, Delete"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            try {
              await deleteCourse(deleteTarget.id);
              setCourses(prev => prev.filter(x => x.id !== deleteTarget.id));
              setDeleteTarget(null);
            } catch (err) { setToast({ msg: err.message, type: 'error' }); setDeleteTarget(null); }
          }}
        />
      )}
    </>
  );
}
